// NeoCore · OKID · Pantalla de test
// -----------------------------------------------------------------------------
// Pantalla standalone para disparar verificaciones OKID de prueba: sube DPI
// (frente) + rostro (selfie) [+ dorso opcional + CUI], llama al edge
// okid-verificar con action:'test' (NO requiere asociado ni persiste en
// verificacion_identidad) y muestra la respuesta cruda + PDF de evidencia.
//
// Sirve para (1) validar el flujo end-to-end contra OKID y (2) ver el payload
// real, que necesitamos para engancharlo después en Verificaciones y en la app.
//
// ⚠️ Cada verificación completa consume 1 crédito OKID. La guía de OKID pide no
// usar imágenes sintéticas contra producción: usar DPIs/rostros reales.
import { useEffect, useState } from 'react'
import { Fingerprint, IdentificationCard, UserFocus, FilePdf, Coins } from '@phosphor-icons/react'
import { supabase } from '@/lib/supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyObj = any

export function NeoCoreOkidTestPage() {
  const [saldo, setSaldo] = useState<string>('…')
  const [dpiFile, setDpiFile] = useState<File | null>(null)
  const [dpi2File, setDpi2File] = useState<File | null>(null)
  const [frontFile, setFrontFile] = useState<File | null>(null)
  const [cui, setCui] = useState('')
  const [working, setWorking] = useState(false)
  const [result, setResult] = useState<AnyObj>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const loadSaldo = () => {
    setSaldo('…')
    supabase.functions.invoke('okid-verificar', { body: { action: 'saldo' } }).then(({ data, error }) => {
      if (error) { setSaldo('error'); return }
      const acc = (data as AnyObj)?.data?.account ?? {}
      const s = acc.CREDIT_AVAILABLE ?? acc.credit_available ?? acc.saldo ?? acc.balance
      setSaldo(s != null ? String(s) : ((data as AnyObj)?.ok ? 'OK' : 'error'))
    }).catch(() => setSaldo('error'))
  }
  useEffect(loadSaldo, [])

  const verificar = async () => {
    if (!dpiFile || !frontFile) { setErr('Subí al menos el DPI (frente) y el rostro.'); return }
    if (cui && !/^\d{13}$/.test(cui.trim())) { setErr('El CUI debe tener 13 dígitos (o dejalo vacío).'); return }
    setWorking(true); setErr(null); setResult(null); setPdfUrl(null)
    try {
      const stamp = Date.now()
      const dpiPath = `_test/${stamp}-dpi.jpg`
      const frontPath = `_test/${stamp}-front.jpg`
      const dpi2Path = dpi2File ? `_test/${stamp}-dpi2.jpg` : undefined

      const ups = [
        supabase.storage.from('kyc').upload(dpiPath, dpiFile, { upsert: true, contentType: dpiFile.type || 'image/jpeg' }),
        supabase.storage.from('kyc').upload(frontPath, frontFile, { upsert: true, contentType: frontFile.type || 'image/jpeg' }),
      ]
      if (dpi2File && dpi2Path) {
        ups.push(supabase.storage.from('kyc').upload(dpi2Path, dpi2File, { upsert: true, contentType: dpi2File.type || 'image/jpeg' }))
      }
      const res = await Promise.all(ups)
      if (res.some((r) => r.error)) { setErr('No se pudieron subir las imágenes al storage.'); setWorking(false); return }

      const { data, error } = await supabase.functions.invoke('okid-verificar', {
        body: {
          action: 'test',
          dpi_path: dpiPath,
          front_path: frontPath,
          dpi2_path: dpi2Path,
          cui: cui.trim() || undefined,
        },
      })
      if (error) { setErr(error.message); setWorking(false); return }
      setResult(data)
      const pp = (data as AnyObj)?.pdf_path
      if (pp) {
        const { data: signed } = await supabase.storage.from('okid').createSignedUrl(pp, 3600)
        if (signed?.signedUrl) setPdfUrl(signed.signedUrl)
      }
      loadSaldo()
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Error inesperado.')
    } finally {
      setWorking(false)
    }
  }

  const reset = () => { setResult(null); setPdfUrl(null); setErr(null); setDpiFile(null); setDpi2File(null); setFrontFile(null); setCui('') }

  return (
    <div style={{ maxWidth: 760, margin: '0 auto', padding: '4px 2px 40px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
        <Fingerprint size={22} weight="duotone" />
        <h2 style={{ margin: 0, fontSize: 19, fontWeight: 800 }}>OKID · Verificación de prueba</h2>
      </div>
      <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--muted-foreground)' }}>
        RENAP + OCR del DPI + comparación facial. No requiere asociado y no persiste en la tabla — es sólo para probar el flujo y ver la respuesta real.
      </p>

      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 13, fontWeight: 700, padding: '5px 12px', borderRadius: 99, background: 'var(--brand-50)', color: 'var(--brand-700, var(--brand))', marginBottom: 18 }}>
        <Coins size={16} weight="fill" /> Créditos OKID disponibles: {saldo}
        <button onClick={loadSaldo} title="Refrescar" style={{ all: 'unset', cursor: 'pointer', marginLeft: 4, opacity: 0.7 }}>↻</button>
      </div>

      {!result && (
        <div style={{ display: 'grid', gap: 16, background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14, padding: 20 }}>
          <FileField label="DPI · frente *" icon={<IdentificationCard size={18} />} file={dpiFile} onPick={setDpiFile} capture="environment" />
          <FileField label="DPI · dorso (opcional)" icon={<IdentificationCard size={18} />} file={dpi2File} onPick={setDpi2File} capture="environment" />
          <FileField label="Rostro · selfie *" icon={<UserFocus size={18} />} file={frontFile} onPick={setFrontFile} capture="user" />

          <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--ink-soft)' }}>
            CUI (opcional · 13 dígitos)
            <input
              value={cui}
              onChange={(e) => setCui(e.target.value.replace(/\D/g, '').slice(0, 13))}
              placeholder="Si se omite, OKID usa el extraído por OCR"
              inputMode="numeric"
              style={{ padding: '9px 11px', borderRadius: 9, border: '1px solid var(--hairline)', fontSize: 14, background: 'var(--bg, #fff)' }}
            />
          </label>

          {err && <div style={{ fontSize: 13, color: 'var(--danger, #a32d2d)', fontWeight: 600 }}>{err}</div>}

          <button className="btn btn-primary" onClick={verificar} disabled={working || !dpiFile || !frontFile}>
            {working ? 'Verificando contra RENAP…' : 'Verificar contra RENAP'}
          </button>
          <p style={{ margin: 0, fontSize: 11, color: 'var(--muted-foreground)' }}>
            Consume 1 crédito sólo si la verificación se completa. No uses imágenes sintéticas: usá DPI y rostro reales.
          </p>
        </div>
      )}

      {result && (
        <div style={{ display: 'grid', gap: 12, background: 'var(--surface)', border: '1px solid var(--hairline)', borderRadius: 14, padding: 20 }}>
          {result.decision && <DecisionBanner decision={result.decision} matchPct={result.match_pct} />}
          {result.match_pct != null && <Row k="Coincidencia facial" v={`${result.match_pct}%`} tone={result.match_pct >= 85 ? 'ok' : result.match_pct >= 70 ? 'warn' : 'warn'} />}
          {result.renap_encontrado != null && <Row k="RENAP" v={result.renap_encontrado ? '✓ resuelto' : '✗ no resuelto'} tone={result.renap_encontrado ? 'ok' : 'warn'} />}
          {result.facial_match != null && <Row k="Rostro coincide" v={result.facial_match ? '✓ sí' : '✗ no'} tone={result.facial_match ? 'ok' : 'warn'} />}
          {result.datos_coinciden != null && <Row k="DPI vs RENAP" v={result.datos_coinciden ? '✓ coinciden' : '✗ discrepan'} tone={result.datos_coinciden ? 'ok' : 'warn'} />}
          {result.vigencia_documento && <Row k="Vigencia DPI" v={String(result.vigencia_documento)} />}
          <Row k="HTTP OKID" v={String(result.http_status ?? '—')} tone={result.http_status === 200 ? 'ok' : 'warn'} />
          {result.verification_id && <Row k="VERIFICATION_ID" v={String(result.verification_id)} />}
          {pdfUrl && (
            <a href={pdfUrl} target="_blank" rel="noreferrer" className="btn btn-ghost" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 7 }}>
              <FilePdf size={17} /> Ver PDF de evidencia →
            </a>
          )}
          <details open style={{ fontSize: 12 }}>
            <summary style={{ cursor: 'pointer', fontWeight: 700, color: 'var(--ink-soft)' }}>Respuesta cruda de OKID</summary>
            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11, background: 'var(--bg-subtle, #f6f6f7)', padding: 12, borderRadius: 9, marginTop: 8, maxHeight: 420, overflow: 'auto' }}>
              {JSON.stringify(result.resultado, null, 2)}
            </pre>
          </details>
          <button className="btn btn-ghost" onClick={reset}>Nueva verificación</button>
        </div>
      )}
    </div>
  )
}

function FileField({ label, icon, file, onPick, capture }: {
  label: string; icon: React.ReactNode; file: File | null;
  onPick: (f: File | null) => void; capture?: 'user' | 'environment'
}) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12, fontWeight: 600, color: 'var(--ink-soft)' }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>{icon} {label}</span>
      <input type="file" accept="image/*" capture={capture} onChange={(e) => onPick(e.target.files?.[0] ?? null)} />
      {file && <span style={{ fontSize: 11, color: 'var(--muted-foreground)', fontWeight: 500 }}>{file.name} · {(file.size / 1024).toFixed(0)} KB · {file.type || 'sin tipo'}</span>}
    </label>
  )
}

function DecisionBanner({ decision, matchPct }: { decision: 'pass' | 'review' | 'fail'; matchPct?: number | null }) {
  const map = {
    pass: { label: 'APROBADO', bg: 'var(--ok-soft, #e6f5ee)', fg: 'var(--ok-ink, #0f8a57)', border: 'rgba(15,138,87,0.3)' },
    review: { label: 'REVISAR', bg: 'var(--warn-soft, #fef3e2)', fg: 'var(--warning, #d97706)', border: 'rgba(217,119,6,0.3)' },
    fail: { label: 'RECHAZADO', bg: 'var(--danger-soft, #fdecec)', fg: 'var(--danger, #a32d2d)', border: 'rgba(163,45,45,0.3)' },
  }[decision] ?? { label: decision.toUpperCase(), bg: 'var(--bg-subtle)', fg: 'var(--ink)', border: 'var(--hairline)' }
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderRadius: 12, background: map.bg, border: `1px solid ${map.border}` }}>
      <span style={{ fontSize: 16, fontWeight: 800, color: map.fg, letterSpacing: 0.3 }}>{map.label}</span>
      {matchPct != null && <span style={{ fontSize: 22, fontWeight: 800, color: map.fg }}>{matchPct}%</span>}
    </div>
  )
}

function Row({ k, v, tone }: { k: string; v: string; tone?: 'ok' | 'warn' }) {
  const color = tone === 'ok' ? 'var(--ok-ink, #0f8a57)' : tone === 'warn' ? 'var(--warning, #d97706)' : 'var(--ink)'
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13 }}>
      <span style={{ color: 'var(--muted-foreground)', fontWeight: 600 }}>{k}</span>
      <span style={{ fontWeight: 700, color }}>{v}</span>
    </div>
  )
}
